import { z } from 'zod';

/** Server Role record. */
export interface ServerRole {
  /** Unique ID for the role in SSRM. */
  readonly id?: number;
  /** ID for the role in Navigate360. */
  externalId: string;
  /** Name of the role. */
  name: string;
}

/** Client Role record. */
export interface Role {
  /** Unique ID for the role in SSRM. */
  readonly id?: number;
  /** ID for the role in Navigate360. */
  externalId: string;
  /** Name of the role. */
  name: string;
}

/**
 * Validates a server Role at runtime without coercing incoming values.
 * Unknown properties are stripped. `satisfies` checks schema compatibility
 * with ServerRole at compile time; it does not perform runtime validation.
 */
export const serverRoleSchema = z.object({
  // The server-assigned ID may be omitted, but must be an integer when present.
  id: z.number().int().optional(),
  // Both fields are required strings; empty strings are allowed by this schema.
  externalId: z.string(),
  name: z.string(),
}) satisfies z.ZodType<ServerRole>;

export const toClientRole = (server: ServerRole): Role => ({
  id: server.id,
  externalId: server.externalId,
  name: server.name,
});

export type ServerRoleCreate = Omit<ServerRole, 'id'>;
export type RoleInput = Omit<Role, 'id'>;

export const toCreateServerRole = (role: RoleInput): ServerRoleCreate => ({
  externalId: role.externalId,
  name: role.name,
});

/**
 * Requires an array of valid server Roles, then maps each to a client Role.
 * Used by httpResource's parse option: validation failures become resource
 * errors, while successful parsing exposes Role[] to the component.
 */
export const rolesResponseSchema = z.array(serverRoleSchema).transform((roles): Role[] => roles.map(toClientRole));
